import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, ScanSearch, Trash2, RefreshCw } from "lucide-react";
import { api, formatPrice, formatArea, asItems, formatApiError } from "@/lib/api";
import { useCompare } from "@/contexts/CompareContext";
import { Button } from "@/components/ui/button";

const ROWS = [
  { key: "price", label: "Price", fmt: (v) => formatPrice(v) },
  { key: "area", label: "Area", fmt: (v, property) => formatArea(v, property.area_unit) },
  { key: "city", label: "City" },
  { key: "locality", label: "Locality" },
  { key: "category", label: "Category" },
  { key: "subcategory", label: "Subcategory" },
  { key: "bedrooms", label: "Bedrooms" },
  { key: "bathrooms", label: "Bathrooms" },
  { key: "parking", label: "Parking" },
  { key: "facing", label: "Facing" },
  { key: "furnished", label: "Furnished" },
  { key: "ownership", label: "Ownership" },
  { key: "age_years", label: "Age (years)" },
  { key: "new_or_resale", label: "Status" },
  { key: "amenities", label: "Amenities", fmt: (v) => (v||[]).join(", ") || "—" },
  { key: "views", label: "Views" },
];

export default function Compare() {
  const { ids, remove, clear } = useCompare();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (ids.length === 0) { setItems([]); setLoading(false); setError(""); return undefined; }
    let live = true;
    setLoading(true);
    setError("");
    api.post("/compare", { property_ids: ids })
      .then(({ data }) => { if (live) setItems(asItems(data)); })
      .catch((requestError) => {
        if (live) {
          setItems([]);
          setError(formatApiError(requestError));
        }
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [ids, reloadKey]);

  return (
    <div className="bg-background min-h-screen" data-testid="compare-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground mb-2">Compare</p>
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-primary">Side by side</h1>
            <p className="text-muted-foreground mt-2">Up to 5 properties · {items.length} selected</p>
          </div>
          {items.length > 0 && <Button variant="outline" onClick={clear} className="font-bold gap-2" data-testid="clear-compare-btn"><Trash2 className="w-4 h-4"/>Clear all</Button>}
        </div>

        {loading ? (
          <div className="border border-border rounded-lg py-24 text-center text-muted-foreground" role="status">Loading comparison…</div>
        ) : error ? (
          <div className="border border-destructive/30 bg-destructive/5 rounded-lg py-20 px-6 text-center" role="alert">
            <h2 className="font-display text-2xl font-bold text-primary">Comparison could not be loaded</h2>
            <p className="text-sm text-muted-foreground mt-2">{error}</p>
            <Button type="button" className="mt-5" onClick={() => setReloadKey((key) => key + 1)}><RefreshCw className="w-4 h-4 mr-2" />Try again</Button>
          </div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-border rounded-lg py-24 text-center">
            <span className="inline-flex h-16 w-16 mx-auto mb-4 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/15 to-primary/5 ring-1 ring-primary/10 text-primary animate-float-soft"><ScanSearch className="w-8 h-8"/></span>
            <h3 className="font-display text-2xl font-bold text-primary">Nothing to compare yet</h3>
            <p className="text-muted-foreground mt-2">Add up to 5 properties from listings to compare them here.</p>
            <Button asChild className="mt-6 font-bold" data-testid="browse-listings-btn"><Link to="/properties">Browse listings</Link></Button>
          </div>
        ) : (
          <div className="overflow-x-auto border border-border rounded-lg">
            <table className="w-full min-w-[760px]">
              <thead>
                <tr>
                  <th className="text-left p-4 bg-secondary border-b border-border min-w-[160px] sticky left-0 bg-secondary"></th>
                  {items.map(p => (
                    <th key={p.property_id} className="p-4 bg-secondary border-b border-border text-left min-w-[280px]" data-testid={`compare-col-${p.property_id}`}>
                      <div className="relative">
                        <button type="button" onClick={() => remove(p.property_id)} aria-label={`Remove ${p.title || "property"} from comparison`} title="Remove from comparison" className="absolute -top-2 -right-2 w-7 h-7 bg-background border border-border rounded-full grid place-items-center hover:bg-destructive hover:text-destructive-foreground transition" data-testid={`remove-compare-${p.property_id}`}><X className="w-3.5 h-3.5"/></button>
                        <Link to={`/properties/${p.property_id}`}>
                          <div className="aspect-[16/10] rounded-md overflow-hidden bg-background mb-3">
                            <img src={(p.images||[])[0] || "/brand/real-place-mark.svg"} alt={(p.images||[])[0] ? p.title : "Property image unavailable"} loading="lazy" decoding="async" className={`w-full h-full ${(p.images||[])[0] ? "object-cover" : "object-contain p-12"}`}/>
                          </div>
                          <p className="font-display font-bold text-primary line-clamp-1">{p.title}</p>
                          <p className="text-xs uppercase tracking-wider text-muted-foreground mt-1">{p.locality}, {p.city}</p>
                          <p className="font-display text-xl font-black text-primary tabular-nums mt-2">{formatPrice(p.price)}</p>
                        </Link>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROWS.map(r => (
                  <tr key={r.key} className="border-b border-border last:border-0">
                    <td className="p-4 text-xs font-bold uppercase tracking-widest text-muted-foreground sticky left-0 bg-background">{r.label}</td>
                    {items.map(p => (
                      <td key={p.property_id} className="p-4 text-sm text-primary font-medium">{r.fmt ? r.fmt(p[r.key], p) : (p[r.key] ?? "—")}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
